import * as React from "react";

import { type PlotPanelState } from "@/shared/state/visualization";

import { seriesDisplayLabels } from "../../Panel/PlotPanel/seriesDisplayLabels";
import { useWorkspaceFiles } from "../../WorkspaceCtx/hooks";
import styles from "../SettingsSection.module.css";

import { PlotSeriesConfig } from "./PlotSeriesConfig";

interface PlotSeriesListProps {
  state: PlotPanelState;
  /**
   * The series whose row a fresh `spotlightKey` expands and pulses, e.g. the
   * one whose settings gear was clicked in the panel's legend.
   */
  spotlightSeriesId?: string;
  /** Triggers the spotlight when it changes to a new truthy value. */
  spotlightKey?: number;
}

/**
 * The rows of the settings sidebar's Series section, one collapsible row per
 * series. Rows expand and collapse independently, and a collapsed row mounts
 * only its header; PlotSeriesConfigFields (PlotSeriesConfig.tsx) documents
 * why collapsed rows unmount their fields.
 *
 * Headers carry the same display labels as the panel's legend, qualified by
 * source file when several series plot the same signal, since a collapsed
 * row's label is all that identifies it.
 */
export function PlotSeriesList({
  state,
  spotlightSeriesId,
  spotlightKey,
}: PlotSeriesListProps) {
  const workspaceFiles = useWorkspaceFiles();

  // The initial set holds the spotlighted row when the list opens with one,
  // else the sole row of a single-series panel, so the common one-series case
  // costs no extra click. Removing a series can leave its id in the set; a
  // stale id matches no row and is harmless.
  const [expandedSeriesIds, setExpandedSeriesIds] = React.useState<
    ReadonlySet<string>
  >(() => {
    if (spotlightSeriesId !== undefined) {
      return new Set([spotlightSeriesId]);
    }
    return new Set(state.data.length === 1 ? [state.data[0].id] : []);
  });

  // Each spotlight expands its row even when the list is already mounted, as
  // when the settings sidebar is open and a legend gear is clicked.
  React.useEffect(() => {
    if (spotlightKey === undefined || spotlightSeriesId === undefined) {
      return;
    }
    setExpandedSeriesIds((current) => {
      if (current.has(spotlightSeriesId)) {
        return current;
      }
      const next = new Set(current);
      next.add(spotlightSeriesId);
      return next;
    });
  }, [spotlightKey, spotlightSeriesId]);

  const displayLabels = React.useMemo(
    () => seriesDisplayLabels(state.data, workspaceFiles),
    [state.data, workspaceFiles],
  );

  const toggleExpanded = (seriesId: string) => {
    setExpandedSeriesIds((current) => {
      const next = new Set(current);
      if (next.has(seriesId)) {
        next.delete(seriesId);
      } else {
        next.add(seriesId);
      }
      return next;
    });
  };

  return (
    <div className={styles.settings}>
      {state.data.map((plotSeries) => (
        <PlotSeriesConfig
          key={plotSeries.id}
          allowedYAxes={state.data.length}
          displayLabel={displayLabels[plotSeries.id]}
          isExpanded={expandedSeriesIds.has(plotSeries.id)}
          onToggle={toggleExpanded}
          panelId={state.id}
          plotSeries={plotSeries}
          spotlightKey={
            plotSeries.id === spotlightSeriesId ? spotlightKey : undefined
          }
        />
      ))}
    </div>
  );
}
